/**
 * filterQuery — the Filter value + sort in the URL query string.
 *
 *   ?color=black,red&price=200-600&sort=price-asc
 *
 * Router-agnostic on purpose (the DS has no react-router): a page reads
 * `location.search` / `useSearchParams()` with `parseFilterQuery` and writes
 * the string from `buildFilterQuery` back — with `replace`, so Back leaves the
 * listing instead of stepping through every filter click.
 *
 * - An options attribute writes its values comma-joined (each value URL-encoded).
 * - A range writes `min-max`; either side may be left out (`300-`, `-400`) and
 *   then means the attribute's bound. Negative bounds are not supported.
 * - The param name is `attribute.param`, else the attribute id; the sort uses
 *   `sortParam` (default `sort`). Every other param is kept as it was.
 */
import { normalizeValue, isRangeActive } from './filterLogic';

const paramOf = (attribute) => attribute.param || attribute.id;

const toParams = (search) => {
  if (search instanceof URLSearchParams) return new URLSearchParams(search);
  const s = typeof search === 'string' ? search : '';
  return new URLSearchParams(s.startsWith('?') ? s.slice(1) : s);
};

const parseRange = (raw, attribute) => {
  const match = /^(\d+(?:\.\d+)?)?-(\d+(?:\.\d+)?)?$/.exec(raw.trim());
  if (!match || (match[1] === undefined && match[2] === undefined)) return undefined;
  const lo = match[1] === undefined ? attribute.min : Number(match[1]);
  const hi = match[2] === undefined ? attribute.max : Number(match[2]);
  return [lo, hi];
};

/**
 * @param {string|URLSearchParams} search  `?a=b` / `a=b` / URLSearchParams
 * @param {Array} attributes               the Filter attributes
 * @param {{ sortOptions?: Array<{value}>, sortParam?: string }} [options]
 * @returns {{ value: object, sort: string|null }}
 */
export const parseFilterQuery = (search, attributes, { sortOptions, sortParam = 'sort' } = {}) => {
  const params = toParams(search);
  const raw = {};
  attributes.forEach((attribute) => {
    const param = params.get(paramOf(attribute));
    if (param === null || param === '') return;
    raw[attribute.id] = attribute.type === 'range'
      ? parseRange(param, attribute)
      : param.split(',').map((v) => v.trim()).filter(Boolean);
  });

  const sortRaw = params.get(sortParam);
  let sort = sortRaw || null;
  if (sort && Array.isArray(sortOptions) && !sortOptions.some((o) => o.value === sort)) sort = null;

  return { value: normalizeValue(attributes, raw), sort };
};

/**
 * @param {{ value?: object, sort?: string|null }} state
 * @param {Array} attributes
 * @param {{ search?: string|URLSearchParams, sortParam?: string, defaultSort?: string }} [options]
 * @returns {string} the query without "?" — '' when nothing is left
 */
export const buildFilterQuery = (
  { value = {}, sort = null } = {},
  attributes,
  { search = '', sortParam = 'sort', defaultSort } = {},
) => {
  const keep = toParams(search);
  attributes.forEach((attribute) => keep.delete(paramOf(attribute)));
  keep.delete(sortParam);

  const clean = normalizeValue(attributes, value);
  const own = [];
  attributes.forEach((attribute) => {
    const v = clean[attribute.id];
    if (v === undefined) return;
    const key = encodeURIComponent(paramOf(attribute));
    if (attribute.type === 'range') {
      if (isRangeActive(attribute, v)) own.push(`${key}=${v[0]}-${v[1]}`);
    } else {
      own.push(`${key}=${v.map(encodeURIComponent).join(',')}`);
    }
  });
  if (sort && sort !== defaultSort) own.push(`${encodeURIComponent(sortParam)}=${encodeURIComponent(sort)}`);

  return [keep.toString(), ...own].filter(Boolean).join('&');
};
